import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle, ClipboardList, Sparkles, Wand2 } from 'lucide-react';
import { 
  Button, 
  Card, 
  CardHeader, 
  CardContent, 
  Input, 
  Select, 
  FormField,
  LoadingSpinner
} from '@/core/components';
import { formatDate } from '@/core/utils';
import type { CreateCarePlanInput } from '../types';
import { COMMON_TASK_TEMPLATES } from '../constants/task-templates';

const carePlanSchema = z.object({
  clientId: z.string().min(1, 'Client ID is required'),
  organizationId: z.string().min(1, 'Organization ID is required'),
  branchId: z.string().optional(),
  name: z.string().min(1, 'Care plan name is required'),
  planType: z.enum(['PERSONAL_CARE', 'COMPANION', 'SKILLED_NURSING', 'THERAPY', 'HOSPICE', 'RESPITE', 'LIVE_IN', 'CUSTOM']),
  effectiveDate: z.string().min(1, 'Effective date is required'),
  expirationDate: z.string().optional(),
  coordinatorId: z.string().optional(),
  notes: z.string().optional(),
});

type CarePlanFormData = z.infer<typeof carePlanSchema>;

export interface CarePlanFormProps {
  initialData?: Partial<CreateCarePlanInput>;
  onSubmit: (data: CreateCarePlanInput) => void;
  isLoading?: boolean;
}

const planTypeOptions = [
  { value: 'PERSONAL_CARE', label: 'Personal Care' },
  { value: 'COMPANION', label: 'Companion' },
  { value: 'SKILLED_NURSING', label: 'Skilled Nursing' },
  { value: 'THERAPY', label: 'Therapy' },
  { value: 'HOSPICE', label: 'Hospice' },
  { value: 'RESPITE', label: 'Respite' },
  { value: 'LIVE_IN', label: 'Live In' },
  { value: 'CUSTOM', label: 'Custom' },
];

export const CarePlanForm: React.FC<CarePlanFormProps> = ({ 
  initialData, 
  onSubmit, 
  isLoading = false 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [clientNeeds, setClientNeeds] = useState(initialData?.notes ?? '');
  const [aiDraft, setAiDraft] = useState('');
  const [suggestedTasks, setSuggestedTasks] = useState<string[]>([]);
  const [riskSuggestions, setRiskSuggestions] = useState<string[]>([]);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<CarePlanFormData>({
    resolver: zodResolver(carePlanSchema as any),
    defaultValues: {
      clientId: initialData?.clientId ?? '',
      organizationId: initialData?.organizationId ?? '',
      branchId: initialData?.branchId ?? '',
      name: initialData?.name ?? '',
      planType: initialData?.planType ?? 'PERSONAL_CARE',
      effectiveDate: initialData?.effectiveDate ?? formatDate(new Date()),
      expirationDate: initialData?.expirationDate ?? '',
      coordinatorId: initialData?.coordinatorId ?? '',
      notes: initialData?.notes ?? '',
    },
  });
  const watchedValues = watch();

  const steps = [
    { title: 'Basic Information', description: 'Care plan details' },
    { title: 'Goals', description: 'Define care goals' },
    { title: 'Interventions', description: 'Plan interventions' },
    { title: 'Review', description: 'Review and submit' },
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onFormSubmit = (data: CarePlanFormData) => {
    onSubmit({
      ...data,
      goals: [],
      interventions: [],
      taskTemplates: COMMON_TASK_TEMPLATES
        .filter((template) => suggestedTasks.includes(template.name))
        .slice(0, 6),
    });
  };

  const detectMissingFields = () => {
    const values = getValues();
    return [
      !values.clientId && 'Client ID',
      !values.organizationId && 'Organization ID',
      !values.name && 'Care plan name',
      !values.effectiveDate && 'Effective date',
      !clientNeeds.trim() && 'Client needs summary',
      !values.expirationDate && 'Expiration/review date',
    ].filter(Boolean) as string[];
  };

  const handleGenerateDraft = async () => {
    const values = getValues();
    const fallbackTasks = COMMON_TASK_TEMPLATES
      .filter((template) =>
        values.planType === 'COMPANION'
          ? ['Companionship Activity', 'Safety Check', 'Visit Documentation'].includes(template.name)
          : ['Morning Personal Care', 'Meal Preparation', 'Safety Check', 'Visit Documentation'].includes(template.name)
      )
      .map((template) => template.name);

    setIsGeneratingDraft(true);
    try {
      const response = await fetch('/api/ai/admin-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'ADMIN',
          page: 'care-plan-create',
          question: 'Create a professional draft care plan summary, suggested task list, and risk assessment prompts for a UK home care client. Use only the supplied fields and needs. Mark all output as draft for human review.',
          context: {
            basicFields: values,
            clientNeeds,
            availableTaskTemplates: COMMON_TASK_TEMPLATES.map((template) => ({
              name: template.name,
              category: template.category,
              description: template.description,
            })),
          },
        }),
      });

      if (!response.ok) throw new Error(`AI returned ${response.status}`);
      const payload = (await response.json()) as { text?: string };
      const draft = payload.text?.trim() || '';
      setAiDraft(draft);
      setValue('notes', [values.notes, draft].filter(Boolean).join('\n\n'));
      setSuggestedTasks(fallbackTasks);
      setRiskSuggestions([
        'Falls risk and safe mobility routes',
        'Medication prompt/documentation risk',
        'Nutrition, hydration and meal support risk',
        'Safeguarding and change-in-condition escalation',
      ]);
      toast.success('AI care plan draft created for review');
    } catch {
      const fallbackDraft = [
        `Draft care plan for ${values.name || 'client'}.`,
        `Plan type: ${values.planType.replace(/_/g, ' ')}.`,
        clientNeeds ? `Client needs: ${clientNeeds}.` : 'Client needs summary required before approval.',
        'Suggested focus: safe personal care, clear task evidence, family/coordinator communication and scheduled review.',
      ].join('\n');
      setAiDraft(fallbackDraft);
      setValue('notes', [values.notes, fallbackDraft].filter(Boolean).join('\n\n'));
      setSuggestedTasks(fallbackTasks);
      setRiskSuggestions(['Falls risk', 'Medication documentation risk', 'Missed visit escalation', 'Safeguarding concern escalation']);
      toast.error('AI was unavailable. Standard draft suggestions were created.');
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader title="AI care plan draft" />
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <label className="text-sm font-semibold text-gray-700">Client needs summary</label>
              <textarea
                value={clientNeeds}
                onChange={(event) => setClientNeeds(event.target.value)}
                rows={4}
                placeholder="Example: needs morning personal care, medication reminders, meal preparation, safe mobility support, family updates."
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              />
            </div>
            <Button type="button" onClick={() => void handleGenerateDraft()} disabled={isGeneratingDraft}>
              <Sparkles className="h-4 w-4 mr-2" />
              {isGeneratingDraft ? 'Generating...' : 'Generate AI draft'}
            </Button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Missing fields</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">
                {detectMissingFields().length === 0 ? 'Ready for review' : detectMissingFields().join(', ')}
              </p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Task suggestions</p>
              <p className="mt-2 text-sm text-emerald-900">{suggestedTasks.length || 0} suggested templates</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Risk prompts</p>
              <p className="mt-2 text-sm text-amber-900">{riskSuggestions.length || 0} review prompts</p>
            </div>
          </div>
          {aiDraft && (
            <div className="mt-4 rounded-lg border border-[#6c3a80]/20 bg-[#6c3a80]/5 p-4">
              <p className="mb-2 flex items-center gap-2 font-semibold text-slate-950">
                <Wand2 className="h-4 w-4 text-[#6c3a80]" />
                Draft for human review
              </p>
              <p className="whitespace-pre-line text-sm leading-6 text-slate-700">{aiDraft}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Client ID"
          error={errors.clientId?.message}
          required
        >
          <Input
            {...register('clientId')}
            placeholder="Enter client ID"
          />
        </FormField>

        <FormField
          label="Organization ID"
          error={errors.organizationId?.message}
          required
        >
          <Input
            {...register('organizationId')}
            placeholder="Enter organization ID"
          />
        </FormField>

        <FormField
          label="Branch ID (Optional)"
          error={errors.branchId?.message}
        >
          <Input
            {...register('branchId')}
            placeholder="Enter branch ID"
          />
        </FormField>

        <FormField
          label="Coordinator ID (Optional)"
          error={errors.coordinatorId?.message}
        >
          <Input
            {...register('coordinatorId')}
            placeholder="Enter coordinator ID"
          />
        </FormField>
      </div>

      <FormField
        label="Care Plan Name"
        error={errors.name?.message}
        required
      >
        <Input
          {...register('name')}
          placeholder="Enter care plan name"
        />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Plan Type"
          error={errors.planType?.message}
          required
        >
          <Select
            options={planTypeOptions}
            value={getValues('planType')}
            onChange={(e) => setValue('planType', e.target.value as CarePlanFormData['planType'])}
          />
        </FormField>

        <FormField
          label="Effective Date"
          error={errors.effectiveDate?.message}
          required
        >
          <Input
            {...register('effectiveDate')}
            type="date"
          />
        </FormField>
      </div>

      <FormField
        label="Expiration Date (Optional)"
        error={errors.expirationDate?.message}
      >
        <Input
          {...register('expirationDate')}
          type="date"
        />
      </FormField>

      <FormField
        label="Notes (Optional)"
        error={errors.notes?.message}
      >
        <textarea
          {...register('notes')}
          placeholder="Enter any additional notes"
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </FormField>
    </div>
  );

  const renderGoals = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        {[
          'Maintain safe daily personal care routine',
          'Reduce avoidable missed tasks through checklist evidence',
          'Monitor changes in wellbeing and escalate concerns',
          'Support nutrition, hydration and safe mobility',
        ].map((goal) => (
          <div key={goal} className="rounded-lg border border-slate-200 bg-white p-4">
            <CheckCircle className="h-5 w-5 text-emerald-600" />
            <p className="mt-3 font-semibold text-slate-950">{goal}</p>
            <p className="mt-1 text-sm text-slate-600">Suggested goal. Confirm, edit or expand after assessment.</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderInterventions = () => (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="flex items-center gap-2 font-semibold text-slate-950">
            <ClipboardList className="h-5 w-5 text-[#6c3a80]" />
            Suggested task templates
          </p>
          <div className="mt-3 space-y-2">
            {(suggestedTasks.length > 0 ? suggestedTasks : COMMON_TASK_TEMPLATES.slice(0, 5).map((template) => template.name)).map((task) => (
              <div key={task} className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">{task}</div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="flex items-center gap-2 font-semibold text-amber-950">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
            Risk assessment prompts
          </p>
          <div className="mt-3 space-y-2">
            {(riskSuggestions.length > 0 ? riskSuggestions : ['Falls risk', 'Medication risk', 'Nutrition/hydration risk', 'Safeguarding escalation']).map((risk) => (
              <div key={risk} className="rounded-md bg-white px-3 py-2 text-sm text-amber-900">{risk}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderReview = () => {
    const watchedValues = getValues();
    const missingFields = detectMissingFields();
    
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium">Review Care Plan</h3>
        
        <Card>
          <CardHeader title="Basic Information" />
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{watchedValues.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Plan Type</dt>
                <dd className="mt-1 text-sm text-gray-900">{watchedValues.planType?.replace(/_/g, ' ')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Client ID</dt>
                <dd className="mt-1 text-sm text-gray-900">{watchedValues.clientId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Organization ID</dt>
                <dd className="mt-1 text-sm text-gray-900">{watchedValues.organizationId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Effective Date</dt>
                <dd className="mt-1 text-sm text-gray-900">{watchedValues.effectiveDate}</dd>
              </div>
              {watchedValues.expirationDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Expiration Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{watchedValues.expirationDate}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="AI readiness checks" />
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <div className={`rounded-lg border p-4 ${missingFields.length ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                <p className="font-semibold text-slate-950">
                  {missingFields.length ? 'Fields still need attention' : 'No required gaps detected'}
                </p>
                <p className="mt-1 text-sm text-slate-700">
                  {missingFields.length ? missingFields.join(', ') : 'Basic fields, needs summary and review dates are present.'}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="font-semibold text-slate-950">Template coverage</p>
                <p className="mt-1 text-sm text-slate-700">
                  {suggestedTasks.length} task templates prepared for the care plan draft.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Next Steps" />
          <CardContent>
            <div className="space-y-3">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-medium">Goals and Interventions</h4>
                <p className="text-sm text-gray-600">After creating the care plan, you can add specific goals and interventions</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-medium">Task Templates</h4>
                <p className="text-sm text-gray-600">Create task templates for recurring care activities</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-medium">Assign Caregivers</h4>
                <p className="text-sm text-gray-600">Assign primary caregivers and backup staff</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={index} className="flex items-center">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                index <= currentStep
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {index + 1}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                index <= currentStep ? 'text-blue-600' : 'text-gray-500'
              }`}>
                {step.title}
              </p>
              <p className="text-xs text-gray-500">{step.description}</p>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-px mx-4 ${
                index < currentStep ? 'bg-blue-600' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit(onFormSubmit)}>
        {currentStep === 0 && renderBasicInfo()}
        {currentStep === 1 && renderGoals()}
        {currentStep === 2 && renderInterventions()}
        {currentStep === 3 && renderReview()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            type="button"
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            Previous
          </Button>

          <div className="flex gap-2">
            {currentStep < steps.length - 1 ? (
              <Button type="button" onClick={nextStep}>
                Next
              </Button>
            ) : (
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
                Create Care Plan
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
